// src/contexts/ThemeContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);
const THEME_STORAGE_KEY = 'theme';

const getSavedTheme = (): Theme | null => {
  try {
    const savedTheme = window.localStorage?.getItem?.(THEME_STORAGE_KEY);
    return savedTheme === 'light' || savedTheme === 'dark' ? savedTheme : null;
  } catch {
    return null;
  }
};

const saveTheme = (theme: Theme): void => {
  try {
    window.localStorage?.setItem?.(THEME_STORAGE_KEY, theme);
  } catch {
    // The theme still works when storage is disabled or unavailable.
  }
};

interface ThemeProviderProps {
  children: ReactNode;
}

const applyThemeToDocument = (currentTheme: Theme): void => {
  if (typeof window !== 'undefined') {
    const root = document.documentElement;
    root.classList.toggle('dark', currentTheme === 'dark');
    root.dataset.theme = currentTheme;
    root.style.colorScheme = currentTheme;

    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', currentTheme === 'dark' ? '#080808' : '#f7f8f5');
  }
};

const getInitialTheme = (): Theme => {
  if (typeof window === 'undefined') return 'light';

  const savedTheme = getSavedTheme();
  if (savedTheme) return savedTheme;

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Keep the server render and the first browser render identical. theme-init.js
  // applies the visual preference before paint; React synchronizes its state once
  // hydration completes.
  const [theme, setThemeState] = useState<Theme>('light');

  const setTheme = useCallback((newTheme: Theme): void => {
    setThemeState(newTheme);

    // Save to localStorage
    if (typeof window !== 'undefined') {
      saveTheme(newTheme);
    }

    // Apply theme to document
    applyThemeToDocument(newTheme);
  }, []);

  const toggleTheme = useCallback((): void => {
    setThemeState(prev => {
      const newTheme = prev === 'light' ? 'dark' : 'light';
      if (typeof window !== 'undefined') {
        saveTheme(newTheme);
      }
      applyThemeToDocument(newTheme);
      return newTheme;
    });
  }, []);

  useEffect(() => {
    const initialTheme = getInitialTheme();
    setThemeState(initialTheme);
    applyThemeToDocument(initialTheme);
  }, []);

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      const handleChange = (e: MediaQueryListEvent) => {
        // Only auto-switch if user hasn't manually set a preference
        const savedTheme = getSavedTheme();
        if (!savedTheme) {
          const nextTheme = e.matches ? 'dark' : 'light';
          setThemeState(nextTheme);
          applyThemeToDocument(nextTheme);
        }
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, []);

  const value: ThemeContextType = {
    theme,
    setTheme,
    toggleTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
